<template>
  <Breadcrumb :items="breadcrumbs" />

  <div class="container-fluid my-3">
    <div class="section-header mb-3">
      <span class="section-label">طلبات الدفع</span>
      <div class="section-divider"></div>
    </div>

    <!-- Filters -->
    <div class="card mb-3">
      <div class="card-body">
        <div class="row g-3 align-items-end">
          <div class="col-md-3">
            <label class="form-label">الحالة</label>
            <select class="form-select" v-model="store.filters.status" @change="reload">
              <option value="">الكل</option>
              <option value="pending">قيد المراجعة</option>
              <option value="approved">مقبول</option>
              <option value="rejected">مرفوض</option>
            </select>
          </div>
          <div class="col-md-4">
            <label class="form-label">بحث بالطالب (الاسم أو الهاتف)</label>
            <input type="text" class="form-control" v-model="search" placeholder="اكتب اسم أو رقم الطالب..." />
          </div>
          <div class="col-md-2">
            <button class="btn btn-outline-secondary w-100" @click="resetFilters">
              <i class="pi pi-refresh me-1"></i> إعادة تعيين
            </button>
          </div>
        </div>
      </div>
    </div>

    <Loading v-if="store.loading" />

    <div v-else class="card">
      <div class="card-body p-0">
        <div v-if="!filteredRequests.length" class="text-center py-5 text-muted">
          <i class="pi pi-inbox" style="font-size:2.5rem;"></i>
          <p class="mt-2">لا توجد طلبات دفع.</p>
        </div>
        <div v-else class="table-responsive">
          <table class="table table-bordered align-middle mb-0">
            <thead class="table-light">
              <tr>
                <th>الطالب</th>
                <th>الخطة</th>
                <th>المبلغ</th>
                <th>رقم العملية</th>
                <th>طريقة الدفع</th>
                <th>الإيصال</th>
                <th>الحالة</th>
                <th>التاريخ</th>
                <th style="width:190px">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in filteredRequests" :key="r.id">
                <td>
                  <div class="fw-bold">{{ r.student?.name || '—' }}</div>
                  <small class="text-muted">{{ r.student?.phone }}</small>
                </td>
                <td>{{ planLabel(r.subscription?.plan_type) }}</td>
                <td>{{ money(r.amount) }}</td>
                <td>{{ r.transaction_number || '—' }}</td>
                <td>{{ r.payment_method?.name || '—' }}</td>
                <td>
                  <button v-if="r.receipt_image_link" class="btn btn-sm btn-outline-info" @click="viewImage(r.receipt_image_link)">
                    <i class="pi pi-image"></i>
                  </button>
                  <span v-else class="text-muted">—</span>
                </td>
                <td><span class="badge" :class="reqBadge(r.status)">{{ reqLabel(r.status) }}</span></td>
                <td><small>{{ date(r.created_at) }}</small></td>
                <td>
                  <button class="btn btn-sm btn-outline-primary me-1" @click="openDetails(r)" title="تفاصيل">
                    <i class="pi pi-eye"></i>
                  </button>
                  <template v-if="r.status === 'pending'">
                    <button class="btn btn-sm btn-success me-1" @click="approve(r)" title="قبول"><i class="pi pi-check"></i></button>
                    <button class="btn btn-sm btn-danger" @click="reject(r)" title="رفض"><i class="pi pi-times"></i></button>
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="card-footer d-flex justify-content-center" v-if="store.pagination && store.pagination.last_page > 1">
        <pagination :data="store.pagination" @pagination-change-page="store.fetchRequests" class="pagination mb-0" />
      </div>
    </div>
  </div>

  <!-- Details dialog -->
  <Dialog v-model:visible="detailsOpen" modal :style="{ width: '40rem' }" :breakpoints="{ '640px': '95vw' }" header="تفاصيل طلب الدفع">
    <div v-if="detailsLoading" class="text-center py-4"><i class="pi pi-spin pi-spinner"></i></div>
    <div v-else-if="details">
      <dl class="row mb-0">
        <dt class="col-4">الطالب</dt><dd class="col-8">{{ details.student?.name }} — {{ details.student?.phone }}</dd>
        <dt class="col-4">المنتج</dt><dd class="col-8">{{ productLabel(details) }}</dd>
        <dt class="col-4">الخطة</dt><dd class="col-8">{{ planLabel(details.subscription?.plan_type) }}</dd>
        <dt class="col-4">المبلغ</dt><dd class="col-8">{{ money(details.amount) }}</dd>
        <dt class="col-4">رقم العملية</dt><dd class="col-8">{{ details.transaction_number || '—' }}</dd>
        <dt class="col-4">طريقة الدفع</dt><dd class="col-8">{{ details.payment_method?.name || '—' }}</dd>
        <dt class="col-4">الحالة</dt><dd class="col-8"><span class="badge" :class="reqBadge(details.status)">{{ reqLabel(details.status) }}</span></dd>
        <dt class="col-4">ملاحظات</dt><dd class="col-8">{{ details.notes || '—' }}</dd>
        <dt class="col-4" v-if="details.reviewer">تمت المراجعة بواسطة</dt><dd class="col-8" v-if="details.reviewer">{{ details.reviewer?.name }}</dd>
      </dl>
      <div class="text-center mt-3" v-if="details.receipt_image_link">
        <img :src="details.receipt_image_link" class="img-fluid rounded border" style="max-height:280px" alt="الإيصال" />
      </div>
    </div>
    <template #footer>
      <template v-if="details && details.status === 'pending'">
        <button class="btn btn-danger" @click="reject(details)"><i class="pi pi-times me-1"></i>رفض</button>
        <button class="btn btn-success" @click="approve(details)"><i class="pi pi-check me-1"></i>قبول</button>
      </template>
      <button class="btn btn-secondary" @click="detailsOpen = false">إغلاق</button>
    </template>
  </Dialog>
</template>

<script>
import Dialog from 'primevue/dialog';
import pagination from 'laravel-vue-pagination';
import Swal from 'sweetalert2';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import Loading from '@/components/global/loading.vue';
import { usePaymentRequestStore } from '@/stores/usePaymentRequestStore.js';
import { planLabel, requestStatusLabel, requestStatusBadge, typeLabel, formatMoney, formatDate, apiError } from '@/composables/subscriptionMeta.js';

export default {
  name: 'PaymentRequestsIndex',
  components: { Dialog, pagination, Breadcrumb, Loading },
  data() {
    return {
      breadcrumbs: [{ name: 'الرئيسية', path: '/' }, { name: 'طلبات الدفع', path: '/payment-requests' }],
      search: '',
      detailsOpen: false,
      detailsLoading: false,
      details: null,
    };
  },
  computed: {
    store() { return usePaymentRequestStore(); },
    filteredRequests() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.store.requests;
      return this.store.requests.filter(r => {
        const name = (r.student?.name || '').toLowerCase();
        const phone = (r.student?.phone || '').toLowerCase();
        return name.includes(q) || phone.includes(q);
      });
    },
  },
  methods: {
    planLabel,
    reqLabel: requestStatusLabel,
    reqBadge: requestStatusBadge,
    money: formatMoney,
    date: formatDate,
    productLabel(r) {
      const s = r.subscription?.subscribable;
      const type = typeLabel(r.subscription?.subscribable_type);
      const name = s?.name || s?.title || '';
      return name ? `${name} (${type})` : type || '—';
    },
    reload() { this.store.fetchRequests(1); },
    resetFilters() {
      this.store.filters.status = '';
      this.search = '';
      this.store.fetchRequests(1);
    },
    viewImage(src) {
      Swal.fire({ imageUrl: src, imageAlt: 'الإيصال', showConfirmButton: false, showCloseButton: true, width: 'auto' });
    },
    async openDetails(r) {
      this.detailsOpen = true;
      this.detailsLoading = true;
      this.details = null;
      try {
        this.details = await this.store.show(r.id);
      } catch (e) {
        Swal.fire({ icon: 'error', title: apiError(e), timer: 2000, showConfirmButton: false });
        this.detailsOpen = false;
      } finally {
        this.detailsLoading = false;
      }
    },
    async approve(r) {
      const { value: note, isConfirmed } = await Swal.fire({
        title: 'قبول الدفع', input: 'textarea', inputLabel: 'ملاحظة (اختياري)',
        inputPlaceholder: 'ملاحظة تُحفظ مع الطلب...', showCancelButton: true,
        confirmButtonText: 'قبول وتفعيل الاشتراك', cancelButtonText: 'إلغاء', confirmButtonColor: '#16a34a',
      });
      if (!isConfirmed) return;
      try {
        await this.store.approve(r.id, note || null);
        this.detailsOpen = false;
        await this.store.fetchRequests(this.store.pagination?.current_page || 1);
        this.store.fetchPendingCount();
        Swal.fire({ icon: 'success', title: 'تم قبول الدفع وتفعيل الاشتراك', timer: 1500, showConfirmButton: false });
      } catch (e) {
        Swal.fire({ icon: 'error', title: apiError(e), timer: 2500, showConfirmButton: false });
      }
    },
    async reject(r) {
      const { value: note, isConfirmed } = await Swal.fire({
        title: 'رفض الدفع', input: 'textarea', inputLabel: 'سبب الرفض (اختياري)',
        inputPlaceholder: 'سبب يظهر للطالب...', showCancelButton: true,
        confirmButtonText: 'رفض الطلب', cancelButtonText: 'إلغاء', confirmButtonColor: '#dc3545',
      });
      if (!isConfirmed) return;
      try {
        await this.store.reject(r.id, note || null);
        this.detailsOpen = false;
        await this.store.fetchRequests(this.store.pagination?.current_page || 1);
        this.store.fetchPendingCount();
        Swal.fire({ icon: 'success', title: 'تم رفض الطلب', timer: 1500, showConfirmButton: false });
      } catch (e) {
        Swal.fire({ icon: 'error', title: apiError(e), timer: 2500, showConfirmButton: false });
      }
    },
  },
  mounted() {
    this.store.fetchRequests(1);
  },
};
</script>
